import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../Components/AuthContext';
import { Clock, Plus, X, MapPin } from 'lucide-react';
import MainLayout from '../../Components/Layout/MainLayout/MainLayout';
import { Button } from '../../Components/ui/button';

const DAYS = [
  { label: 'Monday',    value: 1 },
  { label: 'Tuesday',   value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday',  value: 4 },
  { label: 'Friday',    value: 5 },
  { label: 'Saturday',  value: 6 },
  { label: 'Sunday',    value: 0 },
];

// 30-minute slots from 05:00 to 22:00
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 5; h <= 22; h++) {
    for (let m = 0; m < 60; m += 30) {
      if (h === 22 && m > 0) break;
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return slots;
})();

function formatTime(t) {
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

function emptySchedule() {
  return Object.fromEntries(DAYS.map(d => [d.value, []]));
}

function scheduleFromWindows(windows) {
  const s = emptySchedule();
  for (const w of windows) {
    s[w.dayOfWeek].push({ startTime: w.startTime, endTime: w.endTime });
  }
  return s;
}

const selectClass = "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

const Availability = () => {
  const { tenantDomain } = useParams();
  const { updateTenantDomain } = useAuth();

  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [schedule, setSchedule] = useState(emptySchedule());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    updateTenantDomain(tenantDomain);
    fetchLocations();
  }, [tenantDomain]);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/location`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error();
      const data = await response.json();
      const active = data.filter(l => l.isActive);
      setLocations(active);
      if (active.length > 0) {
        setSelectedLocationId(active[0].locationId);
        await fetchAvailability(active[0].locationId);
      }
    } catch {
      setError('Failed to load locations.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = useCallback(async (locationId) => {
    if (!locationId) return;
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${tenantDomain}/availability?locationId=${locationId}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error();
      const data = await response.json();
      setSchedule(scheduleFromWindows(data));
    } catch {
      setError('Failed to load availability.');
    } finally {
      setLoading(false);
    }
  }, [tenantDomain]);

  const handleLocationChange = async (locationId) => {
    setSelectedLocationId(locationId);
    setError(null);
    setSaveSuccess(false);
    await fetchAvailability(locationId);
  };

  const addWindow = (dayValue) => {
    setSchedule(s => ({
      ...s,
      [dayValue]: [...s[dayValue], { startTime: '09:00', endTime: '17:00' }],
    }));
  };

  const removeWindow = (dayValue, index) => {
    setSchedule(s => ({
      ...s,
      [dayValue]: s[dayValue].filter((_, i) => i !== index),
    }));
  };

  const updateWindow = (dayValue, index, field, value) => {
    setSchedule(s => {
      const windows = [...s[dayValue]];
      windows[index] = { ...windows[index], [field]: value };
      return { ...s, [dayValue]: windows };
    });
  };

  const handleSave = async () => {
    // Flatten schedule to windows array
    const windows = [];
    for (const day of DAYS) {
      for (const w of schedule[day.value]) {
        if (w.startTime >= w.endTime) {
          setError(`Invalid time range on ${day.label}: start must be before end.`);
          return;
        }
        windows.push({ dayOfWeek: day.value, startTime: w.startTime, endTime: w.endTime });
      }
    }

    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${tenantDomain}/availability/location/${selectedLocationId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ windows }),
        }
      );
      if (!response.ok) throw new Error();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      setError('Failed to save availability. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const selectedLocation = locations.find(l => l.locationId === selectedLocationId);

  if (loading && locations.length === 0) {
    return (
      <MainLayout>
        <div className="text-sm text-muted-foreground py-8">Loading...</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Availability</h1>
          <p className="text-sm text-muted-foreground mt-1">Set your weekly availability by location</p>
        </div>
        {selectedLocationId && (
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </div>

      {locations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <MapPin size={36} className="text-muted-foreground mb-3" />
          <p className="text-sm font-medium">No locations yet</p>
          <p className="text-sm text-muted-foreground mt-1">Add a location before setting availability.</p>
        </div>
      ) : (
        <>
          {/* Location selector */}
          <div className="flex flex-wrap gap-2 mb-6">
            {locations.map(loc => (
              <button
                key={loc.locationId}
                onClick={() => handleLocationChange(loc.locationId)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                  selectedLocationId === loc.locationId
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-input hover:border-primary/50 hover:text-foreground'
                }`}
              >
                {loc.name}
              </button>
            ))}
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{error}</div>
          )}
          {saveSuccess && (
            <div className="mb-4 rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
              Availability saved for {selectedLocation?.name}.
            </div>
          )}

          {loading ? (
            <div className="text-sm text-muted-foreground py-8">Loading...</div>
          ) : (
            <div className="flex flex-col divide-y rounded-lg border overflow-hidden">
              {DAYS.map(day => {
                const windows = schedule[day.value] ?? [];
                return (
                  <div key={day.value} className="flex gap-4 px-5 py-4 bg-card">
                    {/* Day label */}
                    <div className="w-28 shrink-0 pt-1.5">
                      <span className={`text-sm font-medium ${windows.length > 0 ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {day.label}
                      </span>
                    </div>

                    {/* Windows */}
                    <div className="flex-1 flex flex-col gap-2">
                      {windows.length === 0 ? (
                        <span className="text-sm text-muted-foreground pt-1.5">Unavailable</span>
                      ) : (
                        windows.map((w, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <select
                              value={w.startTime}
                              onChange={(e) => updateWindow(day.value, i, 'startTime', e.target.value)}
                              className={selectClass}
                            >
                              {TIME_SLOTS.map(t => (
                                <option key={t} value={t}>{formatTime(t)}</option>
                              ))}
                            </select>
                            <span className="text-sm text-muted-foreground">to</span>
                            <select
                              value={w.endTime}
                              onChange={(e) => updateWindow(day.value, i, 'endTime', e.target.value)}
                              className={selectClass}
                            >
                              {TIME_SLOTS.map(t => (
                                <option key={t} value={t}>{formatTime(t)}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => removeWindow(day.value, i)}
                              className="text-muted-foreground hover:text-destructive transition-colors p-1"
                            >
                              <X size={15} />
                            </button>
                          </div>
                        ))
                      )}
                      <button
                        onClick={() => addWindow(day.value)}
                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors w-fit mt-0.5"
                      >
                        <Plus size={13} />
                        Add window
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </MainLayout>
  );
};

export default Availability;
